import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import {
    Network, Users, CalendarDays,
    Settings2, Scale, GitBranch, LayoutDashboard,
} from "lucide-react";
import OrganizationPositions from "./OrganizationPositions";
import PostingManagement from "./PostingManagement";
import LeaveRules from "./hr/LeaveRules";
import LeaveBalancesAdmin from "./hr/LeaveBalancesAdmin";
import HolidaysAdmin from "./hr/HolidaysAdmin";
import ReportingMapAdmin from "./hr/ReportingMapAdmin";
import HrOverview from "./hr/HrOverview";
import { usePermission, NOT_AUTHORISED_MSG, type Extent } from "../../contexts/PermissionContext";

type OrganogramSection =
    | "view" | "postings"
    | "leave_rules" | "balances" | "holidays_admin" | "reporting_map"
    | "hr_overview";

interface SectionDef {
    key: OrganogramSection;
    label: string;
    icon: React.ElementType;
    requires?: { activity: string; extent: Extent };
}

const SECTIONS: SectionDef[] = [
    { key: "view", label: "Organogram View", icon: Network },
    { key: "postings", label: "HR Postings", icon: Users },
    // GRC team persona (admin / manager)
    { key: "leave_rules", label: "Leave Rules", icon: Settings2, requires: { activity: "HR_LEAVE_TYPES", extent: "view+modify" } },
    { key: "balances", label: "Balances", icon: Scale, requires: { activity: "HR_LEAVE_BALANCES", extent: "view+modify" } },
    { key: "holidays_admin", label: "Holidays", icon: CalendarDays, requires: { activity: "HR_HOLIDAYS", extent: "view+modify" } },
    { key: "reporting_map", label: "Reporting Map", icon: GitBranch, requires: { activity: "HR_REPORTING_MAP", extent: "view" } },
    { key: "hr_overview", label: "HR Overview", icon: LayoutDashboard, requires: { activity: "HR_HOLIDAYS", extent: "view" } },
];

// Lightweight catalogue (key + label, no icon/render) so the global header
// search can index every Organogram section and deep-link to it via
// ?tab=organogram&section=<key>. Single source of truth — derived from SECTIONS.
export const ORGANOGRAM_SECTIONS: Array<{ key: OrganogramSection; label: string }> =
    SECTIONS.map(({ key, label }) => ({ key, label }));

const OrganogramTab: React.FC = () => {
    // Honour an initial ?section=<key> deep-link (e.g. the global header search
    // navigates to a specific Organogram section) — fall back to the first
    // section when absent or unknown.
    const [params] = useSearchParams();
    const initialSection = SECTIONS.find(s => s.key === params.get("section"))?.key ?? "view";
    const [active, setActive] = useState<OrganogramSection>(initialSection);

    // React to ?section=<key> changes that arrive after mount (e.g. the global
    // header search navigates here while Organogram is already open — the URL
    // changes but this component does not remount).
    const sectionParam = params.get("section");
    useEffect(() => {
        const match = SECTIONS.find(s => s.key === sectionParam);
        if (match) setActive(match.key);
    }, [sectionParam]);

    // One hook call per gated section (stable order — fixed list, never conditional).
    const gateLeaveTypes = usePermission("HR_LEAVE_TYPES", "view+modify");
    const gateBalances = usePermission("HR_LEAVE_BALANCES", "view+modify");
    const gateHolidays = usePermission("HR_HOLIDAYS", "view+modify");
    const gateReporting = usePermission("HR_REPORTING_MAP", "view");
    const gateHrOverview = usePermission("HR_HOLIDAYS", "view");

    const allowed: Record<OrganogramSection, boolean> = {
        view: true,
        postings: true,
        leave_rules: gateLeaveTypes.allowed,
        balances: gateBalances.allowed,
        holidays_admin: gateHolidays.allowed,
        reporting_map: gateReporting.allowed,
        hr_overview: gateHrOverview.allowed,
    };

    // Convention: every section stays VISIBLE; ones the user has no right to are
    // greyed-out + non-clickable (never hidden). We still gate the rendered content
    // so a stale `active` (e.g. permission revoked mid-session) can't show a section
    // the user lost access to.
    const activeVisible = allowed[active] ? active : "view";

    const renderSection = () => {
        switch (activeVisible) {
            case "view": return <OrganizationPositions />;
            case "postings": return <PostingManagement compact />;
            case "leave_rules": return <LeaveRules />;
            case "balances": return <LeaveBalancesAdmin />;
            case "holidays_admin": return <HolidaysAdmin />;
            case "reporting_map": return <ReportingMapAdmin />;
            case "hr_overview": return <HrOverview />;
            default: return <OrganizationPositions />;
        }
    };

    return (
        <div className="flex h-full overflow-hidden">
            <div className="netbody-card side-nav-card ">
                <div className="side-nav-card-heading">Organogram</div>
                <nav aria-label="Organogram sections">
                    {SECTIONS.map(s => {
                        const denied = !allowed[s.key];
                        return (
                            <button
                                key={s.key}
                                onClick={() => { if (!denied) setActive(s.key); }}
                                disabled={denied}
                                aria-disabled={denied}
                                title={denied ? NOT_AUTHORISED_MSG : undefined}
                                className={`side-panel-menu-item${activeVisible === s.key ? " side-panel-menu-item--active" : ""}${denied ? " side-panel-menu-item--disabled" : ""}`}
                                data-testid={`NAV_organogram_section_${s.key}`}
                            >
                                <s.icon size={14} />
                                {s.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            <div className="netbody-card side-content-card flex flex-col flex-1 min-w-0">
                {renderSection()}
            </div>
        </div>
    );
};

export default OrganogramTab;
